import qs from 'qs';
import { shallowRef } from 'vue';
import User from '../model/User.js';
import Globals from '../model/Globals.js';

class ReserveProAPIService {
    constructor() {
        this.appId = Globals.APP_ID || 'aka';
        this.apiBase = (Globals.API_URL || '').replace(/\/+$/, '');
        this.projectApiBase = `${this.apiBase}/api/${this.appId}/`;

        this._userRef = shallowRef(null);
        this.settings = null;
        this.useEmailAuth = this.getStorageItem('use_email_auth') === "true";
        this.usePhoneAuth = this.getStorageItem('use_phone_auth') === "true";
        this.token = JSON.parse(this.getStorageItem('token') || '{}');
        this.ready = this.init();
    }

    get user() {
        return this._userRef.value;
    }

    set user(value) {
        this._userRef.value = value;
    }

    getAssetUrl(id, params = '') {
        if (!id) return '';
        // Assets are routed through the backend API or app domain
        return `${this.apiBase}/assets/${id}${params ? `${params}` : ''}`;
    }

    getToken() {
        return this.token;
    }

    setToken(token) {
        this.token = token || {};
        this.setStorageItem('token', JSON.stringify(this.token));
    }

    async init() {
        if (this.token?.refresh_token || this.token?.access_token) {
            await this.refreshUser();
        }
    }

    getStorageItem(key, useSession = false) {
        const storage = typeof window !== 'undefined' && typeof (useSession ? sessionStorage : localStorage) !== 'undefined'
            ? (useSession ? sessionStorage : localStorage)
            : null;
        return storage ? storage.getItem(key) : null;
    }

    setStorageItem(key, value, useSession = false) {
        const storage = typeof window !== 'undefined' && typeof (useSession ? sessionStorage : localStorage) !== 'undefined'
            ? (useSession ? sessionStorage : localStorage)
            : null;
        if (storage) {
            storage.setItem(key, value);
        }
    }

    clearStorageItem(key, useSession = false) {
        const storage = typeof window !== 'undefined' && typeof (useSession ? sessionStorage : localStorage) !== 'undefined'
            ? (useSession ? sessionStorage : localStorage)
            : null;
        if (storage) {
            storage.removeItem(key);
        }
    }

    // =========================================================================
    // AUTHENTICATION & SESSION
    // =========================================================================

    async refreshToken() {
        if (!this.token?.refresh_token) {
            return null;
        }

        try {
            const response = await fetch(`${this.projectApiBase}auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: this.token.refresh_token }),
            });

            if (response.ok) {
                const refreshData = await response.json();
                this.setToken(refreshData.data);
                return this.token;
            }
        } catch (error) {
            console.warn('Error during token refresh:', error);
        }

        this.logout();
        return null;
    }

    async refreshUser() {
        if (!this.token?.access_token) {
            this.logout();
            return null;
        }

        const fetchMe = async () => {
            return await fetch(`${this.projectApiBase}users/me`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${this.token.access_token}`,
                    'Content-Type': 'application/json',
                },
            });
        };

        try {
            let response = await fetchMe();

            if (!response.ok && (response.status === 401 || response.status === 403)) {
                const refreshed = await this.refreshToken();
                if (refreshed) {
                    response = await fetchMe();
                }
            }

            if (!response.ok) {
                this.logout();
                return null;
            }

            const data = await response.json();
            this.user = new User(data.data);
            return this.user;
        } catch (error) {
            console.warn('Error fetching user profile:', error);
            this.logout();
            return null;
        }
    }

    async login(email, password) {
        try {
            const response = await fetch(`${this.projectApiBase}auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            if (!response.ok) {
                throw new Error(`Login failed: ${response.statusText}`);
            }
            const data = await response.json();
            this.setToken(data.data);
            return await this.refreshUser();
        } catch (error) {
            this.logout();
            console.error('Error during login:', error);
            throw error;
        }
    }

    isLoggedIn() {
        return this.user != null;
    }

    async ensureUser() {
        await this.ready;
        if (this.user) return this.user;
        if (this.token?.access_token || this.token?.refresh_token) {
            await this.refreshUser();
        }
        return this.user;
    }

    async initAuthWithCode(email, phone) {
        this.user = null;
        this.useEmailAuth = false;
        this.usePhoneAuth = false;

        try {
            const response = await fetch(`${this.projectApiBase}auth/init-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, phone }),
            });
            if (!response.ok) {
                throw new Error(`Error during initAuthWithCode: ${response.statusText}`);
            }

            this.useEmailAuth = Boolean(email && email.length > 0);
            this.usePhoneAuth = Boolean(phone && phone.length > 0);
            this.setStorageItem('use_email_auth', this.useEmailAuth);
            this.setStorageItem('use_phone_auth', this.usePhoneAuth);

            return await response.json();
        } catch (error) {
            console.error('Error during initAuthWithCode:', error);
            throw error;
        }
    }

    async verifyAuthCode(code, verifyUserId = null) {
        try {
            const userId = verifyUserId ?? this.user?.id ?? null;
            if (!userId) {
                throw new Error('User ID is required for verification.');
            }

            const response = await fetch(`${this.projectApiBase}auth/verify-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user: userId, code }),
            });
            if (!response.ok) {
                throw new Error('Niepoprawny kod weryfikacyjny');
            }

            const result = await response.json();
            if (result?.data?.token) {
                this.setToken(result.data.token);
                return await this.refreshUser();
            } else if (Array.isArray(result) && result.length > 0) {
                this.user = new User(result[0]);
                this.user.password = `${this.user.id}-${code}`;
                await this.login(this.user.email, this.user.password);
                return this.user;
            }
            return this.user;
        } catch (error) {
            console.error('Error during verifyAuthCode:', error);
            throw error;
        }
    }

    async loginWithDirectusUserId(userId) {
        try {
            const response = await fetch(`${this.projectApiBase}auth/directus-user`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: userId }),
            });
            if (!response.ok) throw new Error(`User not found: ${userId}`);
            const data = await response.json();
            if (data?.data?.token) {
                this.setToken(data.data.token);
                return await this.refreshUser();
            } else if (data?.data?.email && data?.data?.code) {
                this.user = new User(data.data);
                this.user.password = `${this.user.id}-${this.user.code}`;
                await this.login(this.user.email, this.user.password);
                return this.user;
            }
            return null;
        } catch (error) {
            console.error('Error in loginWithDirectusUserId:', error);
            throw error;
        }
    }

    async resendCode(userId = null, phone = null, email = null) {
        if (!phone && !email) return;
        const response = await fetch(`${this.projectApiBase}resend-code`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: userId, phone, email }),
        });
        return response.ok;
    }

    async logout() {
        this.token = {};
        this.user = null;
        this.setToken(null);
        this.clearStorageItem('use_email_auth');
        this.clearStorageItem('use_phone_auth');
        this.useEmailAuth = false;
        this.usePhoneAuth = false;
        return true;
    }

    // =========================================================================
    // PUBLIC CHECKS & FLOWS (Rate-limited, Zero PII)
    // =========================================================================

    async checkIfEmailExist(email, exceptId = null) {
        if (!email) return false;
        try {
            let url = `${this.projectApiBase}check-email?email=${encodeURIComponent(email)}`;
            if (exceptId) url += `&exceptId=${encodeURIComponent(exceptId)}`;
            const response = await fetch(url);
            if (!response.ok) return false;
            const data = await response.json();
            return !!data?.exists;
        } catch (error) {
            console.warn('Error during checkIfEmailExist:', error);
            return false;
        }
    }

    async checkIfPhoneExist(phone, exceptId = null) {
        if (!phone) return false;
        try {
            let url = `${this.projectApiBase}check-phone?phone=${encodeURIComponent(phone)}`;
            if (exceptId) url += `&exceptId=${encodeURIComponent(exceptId)}`;
            const response = await fetch(url);
            if (!response.ok) return false;
            const data = await response.json();
            return !!data?.exists;
        } catch (error) {
            console.warn('Error during checkIfPhoneExist:', error);
            return false;
        }
    }

    async checkIfUserIdExist(userId) {
        if (!userId) return false;
        try {
            const url = `${this.projectApiBase}check-user-id?id=${encodeURIComponent(userId)}`;
            const response = await fetch(url);
            if (!response.ok) return false;
            const data = await response.json();
            return !!data?.exists;
        } catch (error) {
            console.warn('Error during checkIfUserIdExist:', error);
            return false;
        }
    }

    // =========================================================================
    // DOMAIN API: CATALOG, OFFERS, AGREEMENTS, TAGS
    // =========================================================================

    async getOfferBySlug(slug) {
        if (!slug) return null;
        try {
            const url = `${this.projectApiBase}offers/slug/${encodeURIComponent(slug)}`;
            const response = await fetch(url);
            if (!response.ok) return null;
            const data = await response.json();
            return data?.data || null;
        } catch (error) {
            console.error('Error fetching offer by slug:', error);
            return null;
        }
    }

    async getOfferById(id) {
        if (!id) return null;
        try {
            const url = `${this.projectApiBase}offers/id/${encodeURIComponent(id)}`;
            const response = await fetch(url);
            if (!response.ok) return null;
            const data = await response.json();
            return data?.data || null;
        } catch (error) {
            console.error('Error fetching offer by id:', error);
            return null;
        }
    }

    async getGroupSlug(groupId) {
        if (!groupId) return null;
        try {
            const url = `${this.projectApiBase}offers/group-slug/${encodeURIComponent(groupId)}`;
            const response = await fetch(url);
            if (!response.ok) return null;
            const data = await response.json();
            return data?.data || null;
        } catch (error) {
            console.error('Error fetching group slug:', error);
            return null;
        }
    }

    async getAllGroups() {
        try {
            const url = `${this.projectApiBase}offers/groups`;
            const response = await fetch(url);
            if (!response.ok) return [];
            const data = await response.json();
            return data?.data || [];
        } catch (error) {
            console.error('Error fetching all groups:', error);
            return [];
        }
    }

    async getAgreements(condkey = null) {
        const queryString = condkey ? `?condition=${encodeURIComponent(condkey)}` : '';
        const url = `${this.projectApiBase}agreements${queryString}`;
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Error fetching agreements: ${response.statusText}`);
        }
        const data = (await response.json())?.data || [];
        return data.map(a => ({
            id: a.id,
            title: a.title,
            required: a.required,
            condition: a.condition,
            content: a.content,
            acceptance: false,
            document_url: a.document_url,
            sort: a.sort || 0,
        }));
    }

    async getPathTags() {
        const url = `${this.projectApiBase}tags/paths`;
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Error fetching path tags: ${response.statusText}`);
        }
        const data = await response.json();
        return data?.data || [];
    }

    async getCoupon(code) {
        if (!code) return null;
        try {
            const url = `${this.projectApiBase}coupons/validate/${encodeURIComponent(code)}`;
            const response = await fetch(url);
            if (!response.ok) return null;
            const data = await response.json();
            const list = data?.data || [];
            return list.length > 0 ? list[0] : null;
        } catch (error) {
            console.error('Error fetching coupon:', error);
            return null;
        }
    }

    async fetchSettings(id = 1) {
        return this.getSettings(id);
    }

    async getSettings(id = 1) {
        try {
            const url = `${this.projectApiBase}settings?id=${encodeURIComponent(id)}`;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Error fetching settings: ${response.statusText}`);
            }
            const data = await response.json();
            return data?.data || null;
        } catch (e) {
            console.error('getSettings error:', e);
            throw e;
        }
    }

    async getOffDays(key) {
        try {
            const url = `${this.projectApiBase}off-days?key=${encodeURIComponent(key)}`;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Error fetching off days: ${response.statusText}`);
            }
            const data = await response.json();
            return data?.data || [];
        } catch (e) {
            console.error('getOffDays error:', e);
            throw e;
        }
    }

    async getGroupAvailability(groupId) {
        try {
            const url = `${this.projectApiBase}groups/${encodeURIComponent(groupId)}/availability`;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Error fetching group availability: ${response.statusText}`);
            }
            const data = await response.json();
            return data?.data || null;
        } catch (e) {
            console.error('getGroupAvailability error:', e);
            throw e;
        }
    }

    async getGroupNamesByIds(groupIds) {
        try {
            if (!groupIds || groupIds.length === 0) return [];
            const idsParam = Array.isArray(groupIds) ? groupIds.join(',') : groupIds;
            const url = `${this.projectApiBase}groups/names?ids=${encodeURIComponent(idsParam)}`;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Error fetching group names: ${response.statusText}`);
            }
            const data = await response.json();
            return data?.data || [];
        } catch (e) {
            console.error('getGroupNamesByIds error:', e);
            throw e;
        }
    }

    // =========================================================================
    // DOMAIN API: ORDERS & PROFILES (User session required)
    // =========================================================================

    async getOrders() {
        await this.ensureUser();
        if (!this.token?.access_token) return [];

        try {
            const url = `${this.projectApiBase}orders`;
            const response = await fetch(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token.access_token}`,
                },
            });
            if (!response.ok) {
                throw new Error(`Error fetching orders: ${response.statusText}`);
            }
            const data = await response.json();
            return data?.data || [];
        } catch (error) {
            console.error('Error fetching orders:', error);
            throw error;
        }
    }

    async getOrderById(orderId) {
        await this.ensureUser();
        if (!this.token?.access_token) return null;

        try {
            const url = `${this.projectApiBase}orders/${orderId}`;
            const response = await fetch(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token.access_token}`,
                },
            });
            if (!response.ok) {
                throw new Error(`Error fetching order: ${response.statusText}`);
            }
            const data = await response.json();
            return data?.data || null;
        } catch (error) {
            console.error('Error fetching order by id:', error);
            throw error;
        }
    }

    async createOrder(orderData) {
        await this.ensureUser();
        if (!this.token?.access_token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.projectApiBase}orders`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token.access_token}`,
                },
                body: JSON.stringify(orderData),
            });
            if (!response.ok) {
                throw new Error(`Error creating order: ${response.statusText}`);
            }
            const data = await response.json();
            return data?.data || null;
        } catch (error) {
            console.error('Error creating order:', error);
            throw error;
        }
    }

    async getOrder(orderId, customerId = null) {
        try {
            const url = `${this.projectApiBase}order-status/${orderId}/${customerId ?? ''}`;
            const response = await fetch(url, {
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.ok) {
                throw new Error('Order not found');
            }
            const data = await response.json();
            return data.data;
        } catch (error) {
            console.error('Error fetching order:', error);
            throw error;
        }
    }

    async getMyProfiles() {
        await this.ensureUser();
        if (!this.token?.access_token) return [];

        try {
            const url = `${this.projectApiBase}profiles`;
            const response = await fetch(url, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token.access_token}`,
                },
            });
            if (!response.ok) {
                throw new Error(`Error fetching profiles: ${response.statusText}`);
            }
            const data = await response.json();
            return data?.data || [];
        } catch (error) {
            console.error('Error fetching profiles:', error);
            throw error;
        }
    }

    async createProfile(profileData) {
        await this.ensureUser();
        if (!this.token?.access_token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.projectApiBase}profiles`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token.access_token}`,
                },
                body: JSON.stringify(profileData),
            });
            if (!response.ok) {
                throw new Error(`Error creating profile: ${response.statusText}`);
            }
            const data = await response.json();
            return data?.data || null;
        } catch (error) {
            console.error('Error creating profile:', error);
            throw error;
        }
    }

    async updateProfile(id, profileData) {
        await this.ensureUser();
        if (!this.token?.access_token) {
            throw new Error('User not authenticated');
        }

        try {
            const response = await fetch(`${this.projectApiBase}profiles/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token.access_token}`,
                },
                body: JSON.stringify(profileData),
            });
            if (!response.ok) {
                throw new Error(`Error updating profile: ${response.statusText}`);
            }
            const data = await response.json();
            return data?.data || null;
        } catch (error) {
            console.error('Error updating profile:', error);
            throw error;
        }
    }

    async getGroupsForProfile(profileId) {
        await this.ensureUser();
        if (!this.token?.access_token) return [];

        try {
            const url = `${this.projectApiBase}participants/groups-for-profile`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token.access_token}`,
                },
                body: JSON.stringify({ profileId }),
            });
            if (!response.ok) {
                throw new Error(`Error fetching groups for profile: ${response.statusText}`);
            }
            const data = await response.json();
            return data?.data || [];
        } catch (error) {
            console.error('Error fetching groups for profile:', error);
            throw error;
        }
    }

    async validateEarlyBirds(groupIds) {
        await this.ensureUser();
        if (!this.token?.access_token) return false;

        try {
            const url = `${this.projectApiBase}participants/validate-early-birds`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token.access_token}`,
                },
                body: JSON.stringify({ groupIds }),
            });
            if (!response.ok) return false;
            const data = await response.json();
            return !!data?.exists;
        } catch (error) {
            console.error('Error validating early birds:', error);
            return false;
        }
    }

    async updateCustomer(item) {
        await this.ensureUser();
        if (!this.token?.access_token) return null;

        try {
            const response = await fetch(`${this.projectApiBase}users/me`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token.access_token}`,
                },
                body: JSON.stringify(item),
            });
            if (response.ok && response.status === 200) {
                return await this.refreshUser();
            }
            if (!response.ok && response.status === 400) {
                throw new Error('Problem podczas zapisywania danych. Email lub telefon jest już zarejestrowany.');
            }
            if (!response.ok && response.status === 401) {
                throw new Error('Problem z autoryzacją. Zaloguj się ponownie.');
            }
            if (!response.ok) {
                throw new Error(`Problem serwera. Kod: ${response.status}`);
            }
        } catch (e) {
            console.error('updateCustomer failed:', e);
            throw e;
        }
    }

    capitalizePhrase(text) {
        if (!text) return '';
        const words = text.toLowerCase().split(' ');
        const result = words.map(word => {
            if (word.includes('-')) {
                const subparts = word.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1));
                return subparts.join('-');
            }
            return word.charAt(0).toUpperCase() + word.slice(1);
        });
        return result.join(' ').trim();
    }
}

const rpAPI = new ReserveProAPIService();
export default rpAPI;
